import { computed, effect, EffectRef, inject, Injectable, signal, Signal, untracked, WritableSignal } from '@angular/core';
import { MapViewerNode } from '@3ddv/dvm-internal';
import { MenuContentItem } from '@models/interfaces/components/Menu.interface';
import { Configuration } from '../../models/interfaces/configuration/index.interface';
import { SearchOption } from '../../models/types/components/Search.type';
import { AppViews } from '../../models/enums/dvm/DvmViews.enum';
import { DvmService } from '../dvm.service';
import { UiService } from '../ui.service';
import { SearchTypes } from '../../models/enums/components/Search/SearchTypes.enum';
import { SeatmapDisplay } from '@models/types/components/SeatmapDisplay.type';
import { MenuService } from './menu.service';
import { AmenitiesService } from '@services/amenities.service';
import { isInteriorViewId } from '@helpers/utils/dvm-node.util';
import { AppNodes } from '@models/enums/dvm/DvmNodes.enum';

@Injectable()
export class SearchService {

  // SERVICES
  public readonly  dvmService:        DvmService                             = inject(DvmService);                          // DVM SERVICE
  private readonly config:            Signal<Configuration['Ui']>            = inject(UiService).uiConfig;                  // UI CONFIGURATION
  private readonly menuService:       MenuService                            = inject(MenuService);
  private readonly amsService:        AmenitiesService                       = inject(AmenitiesService);


  // SEARCH CONFIGURATION
  public readonly searchConfig:       Signal<Configuration['Ui']['Search']>  = computed((): Configuration['Ui']['Search'] => this.config()['Search']); // SEARCH CONFIGURATION
  private readonly seatmapDisplay:    Signal<SeatmapDisplay> = computed((): SeatmapDisplay=> this.config().SeatmapDisplay);
  private readonly view3dDisplayEnabled:  Signal<boolean>         = computed((): boolean => this.config().View3dDisplay.enabled);
  public readonly customUnavailableIndex: Signal<ReadonlyMap<string, ReadonlySet<string>>> = computed(() => {
    const customAvailability = this.config().CustomAvailability;
    if (!customAvailability.enabled) return new Map();

    return new Map(
      Object.entries(customAvailability.data).map(([type, nodeIds]) => [
        type,
        new Set(nodeIds ?? []),
      ])
    );
  });


  // SEARCH STATE
  public readonly searchActive:       WritableSignal<boolean | null>         = signal(null);          // Estado del Search (Abierto/Cerrado)
  public readonly searchValue:        WritableSignal<SearchOption| null>     = signal(null);          // Valor del Search (Normalmente una Search Option con label y value)
  private focusedSection: { node: MapViewerNode; menuItem: MenuContentItem | null } | null = null;

  // GLOBAL STATE
  public readonly view:               Signal<AppViews|null>                  = this.dvmService.view;  // Vista actual del DVM
  public readonly isAnimating:        WritableSignal<boolean>                = signal(false);         // Estado de la animación del Search
  public readonly duration:           number                                 = 300;                   // Duración de la animación del Search                  
  

  // SEARCH METHODS
  
  /**
   * Método básico del servicio para activar o desactivar el Search. 
   * Si se llama mientras se está animando, no se ejecutará.
   * @returns {void}
   */
  public toggleSearch(): void {

    if(this.isAnimating()) return;

    if (!this.searchActive()) this.clearFocusedSection();
    this.searchActive.update(val => !val);

  }

  /**
   * Método que establece el valor de SearchValue.
   * Este valor puede ser un Search Option o un valor null a modo reset.
   * Útil para guardar el valor de la búsqueda en el servicio.
   * 
   * @param {SearchOption | null} value 
   */
  public setSearchValue(value: SearchOption | null): void {

    this.searchValue.set(value); 

  }

  public restoreFocusedSection(): void {
    const focus = this.focusedSection;
    if (!focus) return;

    if (
      this.view() !== AppViews.Topview ||
      this.menuService.itemSelected() !== focus.menuItem ||
      this.dvmService.viewer.getNodeById(focus.node.id) !== focus.node ||
      focus.node.state === 'unavailable' ||
      this.isCustomUnavailable(AppNodes.Section, focus.node.id)
    ) {
      this.focusedSection = null;
      return;
    }

    this.dvmService.replaceSelection(focus.node.id);
  }

  public clearFocusedSection(): void {
    const focus = this.focusedSection;
    this.focusedSection = null;
    if (
      focus &&
      this.menuService.itemSelected() === focus.menuItem &&
      this.dvmService.viewer.getNodeById(focus.node.id) === focus.node &&
      focus.node.state === 'selected'
    ) this.dvmService.viewer.unselect(focus.node.id);
  }

  private readonly releaseFocusedSection: EffectRef = effect((): void => {
    const view = this.view();
    const menuItem = this.menuService.itemSelected();
    if (this.focusedSection?.menuItem !== menuItem) this.focusedSection = null;
    if (view !== AppViews.Topview) untracked(() => this.clearFocusedSection());
  });

  /**
   * Devuelve los IDs de los nodos que siguen siendo funcionalmente buscables.
   * Los nodos seleccionados forman parte de la disponibilidad efectiva del mapa.
   *
   * @param type Tipo de nodo DVM o `null` para consultar todos los tipos.
   */
  public getSearchableNodeIds(type: string | null = null): string[] {
    const availableIds = this.dvmService.viewer
      .getNodesByState(type, 'available')
      .map(node => node.id);
    const selectedIds = this.dvmService.viewer
      .getNodesByState(type, 'selected')
      .map(node => node.id);

    return [...new Set([...availableIds, ...selectedIds])];
  }

  /**
   * Filtra IDs contra la disponibilidad efectiva del viewer.
   * Las vistas interiores se conservan porque pueden existir solo en el viewer 3D.
   */
  public filterSearchableNodeIds(nodeIds: string[]): string[] {
    const searchableIds = new Set(this.getSearchableNodeIds());

    return nodeIds.filter(nodeId => isInteriorViewId(nodeId) || searchableIds.has(nodeId));
  }

  public isCustomUnavailable(type: AppNodes, nodeId: string): boolean {
    return this.customUnavailableIndex().get(type)?.has(nodeId) ?? false;
  }

  public filterCustomAvailableNodeIds(type: AppNodes, nodeIds: string[]): string[] {
    const unavailable = this.customUnavailableIndex().get(type);
    return unavailable ? nodeIds.filter((nodeId) => !unavailable.has(nodeId)) : nodeIds;
  }

  /**
   * Método principal del buscador por el cual navega a la sección buscada.
   * El método acepta un SearchOption o un string como parámetro. 
   * Cambiará el estado del Search a cerrado y retorna el observable de la carga del mapa para
   * poder suscribirse a él y realizar acciones adicionales.
   * 
   * @param {SearchOption | string} search 
   * @returns {Observable<MapViewerLoadObject>}
   */
  public navigateToSearch(search: SearchOption | string, nodeId?: string): void {
    
    const searchValue: string = Object.prototype.hasOwnProperty.call(search, 'value') ? (search as SearchOption).value : search as string;
    const isIntSection = isInteriorViewId(searchValue);
    this.clearFocusedSection();
    this.toggleSearch();

    
    // If the selected item is not related to the searched section, clear the selection
    const itemSelected = this.menuService.itemSelected();
    if (
      itemSelected &&
      itemSelected.relatedSeats &&
      itemSelected.relatedSeats.section &&
      !itemSelected.relatedSeats.section.includes(searchValue)
    ) {
      this.menuService.clearCurrentSelection();
    }

    const focusInTopview = this.view() === AppViews.Topview && this.searchConfig().type === SearchTypes.Simple && !nodeId;
    if (focusInTopview && this.focusSection(searchValue)) return;

    if (this.dvmService.selectNodeInTopview(searchValue)) {
      this.dvmService.replaceSelection(searchValue);
      return;
    }

    if (isIntSection) {

      this.dvmService.load3dView(searchValue).subscribe({
        error: (): void => undefined
      });
      return;
    }

    if (this.dvmService.apply3DView(searchValue)) {
      this.loadPanoOrSelect(searchValue);
      return;
    }

    if (focusInTopview) return;

    if( this.view() === AppViews.Main ){

      this.dvmService.goTo(searchValue);

    } else {
      if(this.seatmapDisplay().enabled) {

        this.dvmService.loadMap(searchValue).subscribe({
          next: (() => {
            this.amsService.checkSectionAmenity(this.dvmService.viewer.getNodeById(this.dvmService.currentMap!)!.original_id);
            //if advanced search, select seat
            if(nodeId) {
              this.dvmService.viewer.select(nodeId);
              window.setTimeout((): void => {
                this.dvmService.goTo(nodeId!);
              }, this.duration + 250);
            }
          }),
          error: (): void => this.loadPanoOrSelect(searchValue)
        });
      }


    }
            
  }

  /**
   * Encuadra y resalta la seccion buscada en topview.
   *
   * @returns `true` si el nodo es una seccion enfocable.
   */
  private focusSection(searchValue: string): boolean {
    const section = this.dvmService.viewer.getNodeById(searchValue);
    if (section?.type !== AppNodes.Section || section.state === 'unavailable') return false;

    const focus = { node: section, menuItem: this.menuService.itemSelected() };
    this.focusedSection = focus;
    void this.dvmService.goTo(searchValue)
      .then((focused: boolean): void => {
        if (!focused && this.focusedSection === focus) this.clearFocusedSection();
      })
      .catch((error: unknown): void => {
        if (this.focusedSection === focus) this.clearFocusedSection();
        console.error('Error focusing search section', searchValue, error);
      });
    return true;
  }

  /**
   * Abre el pano si existe o mantiene la seccion seleccionada en topview.
   */
  private loadPanoOrSelect(nodeId: string): void {
    this.dvmService.load3dViewOrSelect(nodeId, this.view3dDisplayEnabled()).subscribe({
      error: (error: unknown): void => console.error('Error checking or loading 3D view for search result', nodeId, error)
    });
  }

  /**
   * Selecciona la sección o asiento buscado en el DVM.
   * Para ello, se basa en la vista actual del DVM y el tipo de searcher que se esté utilizando.
   * 
   * Si el search es simple, el valor de búsqueda es el value del SearchOption.
   * 
   * Si es avanzado, el valor de búsqueda varía ya que en el buscador avanzado guarda el search Option tal que:
   * {label: 'section-row-seat', value: 'section'}, de este modo podemos hacer navigateToSearch con el value de la sección. 
   * 
   * Sin embargo, para seleccionar el nodo, en la topview escogeremos el value (para seleccionar la sección) y en el seatmap
   * cogeremos la label para seleccionar el nodo exacto.
   * 
   * @returns {void}
   */
  private selectSearchedSection(): void {

    // Si no hay valor de búsqueda, no hacemos nada.
    if(!this.searchValue()) return;
    
    const currentView:      AppViews | null = this.view(),              
          searchType:       SearchTypes     = this.searchConfig().type; 
    let   selectedSearch!:  string; 

    // Si el buscador es avanzado, seleccionamos el valor de búsqueda en función de la vista. 
    if(searchType === SearchTypes.Advanced) {

      // Si estamos en Topview, seleccionamos la sección buscada (value), si no cogemos el nodo exacto (label).
      selectedSearch = currentView === AppViews.Topview ? this.searchValue()!.value : this.searchValue()!.label!;

    } 
    // Si no, seleccionamos el value.
    else {

      selectedSearch = this.searchValue()!.value!;

    }

    this.dvmService.viewer.select(selectedSearch);

  }

  /**
   * Efecto que reacciona al cambio de vista del DVM.
   * Si la vista es Topview, selecciona la sección buscada.
   */
  public onViewChange: EffectRef = effect((): void => {
  
      const view: AppViews | null = this.view();
  
      if(view === null || !this.searchValue()) return;
  
      switch(view) {
  
        case AppViews.Topview:

         this.selectSearchedSection();

        break;
  
      }
  
  
  });
  
}
